import { AudioSample, AudioSampleSource, BufferTarget, Output, WebMOutputFormat } from "mediabunny";
import { frameCount, numberOfChannels, type PcmAudio } from "../../types";
import { resampleAudio } from "../resample";

/** Sample rates the Opus encoder accepts as input. Anything else is resampled. */
const OPUS_INPUT_RATES = [8000, 12000, 16000, 24000, 48000];

/**
 * Snap to an Opus-legal input rate: the rate itself if already legal, else
 * 48 kHz (Opus's internal rate). Pure.
 */
export function nearestOpusSampleRate(rate: number): number {
  return OPUS_INPUT_RATES.includes(rate) ? rate : 48000;
}

export interface OpusEncodeOptions {
  /** Target bitrate in bits/sec. Defaults to 96 kbps. */
  bitrate?: number;
}

/**
 * Encode PCM to Opus in a WebM container using the native WebCodecs
 * `AudioEncoder` (hardware-assisted, no WASM). Browser-only.
 *
 * Opus only accepts a fixed set of input sample rates, so the audio is
 * resampled first when necessary. Output plays in Chrome/Edge/Firefox; Safari
 * may not play WebM inline but the download is still a valid file.
 */
export async function encodeOpus(
  audio: PcmAudio,
  options: OpusEncodeOptions = {}
): Promise<ArrayBuffer> {
  if (typeof globalThis.AudioEncoder === "undefined") {
    throw new Error("Opus encoding is not supported in this browser");
  }
  const bitrate = options.bitrate ?? 96_000;

  const targetRate = nearestOpusSampleRate(audio.sampleRate);
  const source =
    audio.sampleRate === targetRate
      ? audio
      : await resampleAudio(audio, { sampleRate: targetRate });

  const channels = Math.max(1, numberOfChannels(source));
  const frames = frameCount(source);

  const output = new Output({
    format: new WebMOutputFormat(),
    target: new BufferTarget(),
  });
  const audioSource = new AudioSampleSource({ codec: "opus", bitrate });
  output.addAudioTrack(audioSource);

  try {
    await output.start();
    // Feed ~0.5s planar blocks. Mediabunny timestamps are seconds, unlike
    // WebCodecs' microseconds. Await add() to bound the encoder queue.
    const blockFrames = Math.max(1, Math.floor(targetRate / 2));
    for (let offset = 0; offset < frames; offset += blockFrames) {
      const count = Math.min(blockFrames, frames - offset);
      const planar = new Float32Array(count * channels);
      for (let c = 0; c < channels; c++) {
        const channel = source.channelData[c];
        if (channel) planar.set(channel.subarray(offset, offset + count), c * count);
      }
      const sample = new AudioSample({
        format: "f32-planar",
        sampleRate: targetRate,
        numberOfChannels: channels,
        timestamp: offset / targetRate,
        data: planar,
      });
      try {
        await audioSource.add(sample);
      } finally {
        sample.close();
      }
    }
    audioSource.close();
    await output.finalize();
  } catch (error) {
    // Preserve the encoding error even if resource cleanup also fails.
    await output.cancel().catch(() => {});
    throw error;
  }

  const data = output.target.buffer;
  if (!data) throw new Error("Opus encoding produced no output.");
  return data;
}
